import {Injectable} from '@angular/core';
import {DeviceEnrollmentSession} from './device-enrollment.models';

/**
 * The rotating register-enrolment code, computed in the browser (#279).
 *
 * ### What this mirrors, exactly
 *
 * The server's `RotatingEnrolmentCode` (`CheqMe.Server.Infrastructure.Cryptography.TwoFactor`): RFC 6238 over
 * the session secret with **HMAC-SHA-256**, a counter of `floor(unixSeconds / windowSeconds)`, RFC 4226 dynamic
 * truncation and **eight** digits. The e2e reference in `scripts/e2e/appium/lib/api.ts` (`rotatingCode`) is the
 * same computation on Node's `crypto`; this file is the Web Crypto rendering of it. All three are pinned by one
 * cross-language golden vector — base32 `GEZDGNBVGY3TQOJQGEZDGNBVGY3TQOJQ` at `2026-01-01T00:00:00Z` is
 * `05578892` — in `DeviceEnrollmentTests` (C#) and `rotating-enrollment-code.spec.ts` (here). Change one side
 * and that vector is what tells you the other side no longer agrees.
 *
 * ### Why the pure parts are exported functions and only the HMAC is a service
 *
 * `crypto.subtle` is asynchronous and completes off the JavaScript thread, so nothing inside Angular's
 * `fakeAsync` zone can make it resolve on a `tick()`. The panel that rotates the code therefore takes the
 * computation through {@link RotatingEnrollmentCodeService}, which a spec replaces with a deterministic stub
 * to drive time — while the algorithm itself is proven once, against the golden vector, with the real Web
 * Crypto in an ordinary `async` spec. The base32 decoding, the truncation and the window arithmetic are pure
 * and synchronous, so they are plain functions with plain tests.
 */

/** Digits in the rotating half. Mirrors `RotatingEnrolmentCode.Digits`. */
export const ROTATING_CODE_DIGITS = 8;

/** The rotation cadence used when a session does not say (it always does; this is the documented default). */
export const DEFAULT_WINDOW_SECONDS = 60;

/** Joins the session id and the rotating digits. Mirrors `Shared.Core.Models.Device.EnrollmentCode.Separator`. */
export const ENROLLMENT_CODE_SEPARATOR = '.';

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/**
 * RFC 4648 base32 decode — unpadded or padded, any case, for the session secret. Characters outside the
 * alphabet are dropped rather than refused, matching the e2e reference: the secret is server-minted and never
 * typed, so a stray character is a transport artefact, not an attack surface.
 */
export function base32Decode(encoded: string): Uint8Array {
  const clean = encoded.replace(/=+$/, '').toUpperCase().replace(/[^A-Z2-7]/g, '');
  const out: number[] = [];
  let bits = 0;
  let value = 0;

  for (const ch of clean) {
    value = (value << 5) | BASE32_ALPHABET.indexOf(ch);
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }

  return Uint8Array.from(out);
}

/** The rotation window covering `atMs`: Unix seconds over the window length. Mirrors `Totp.CounterAt`. */
export function windowIndexAt(atMs: number, windowSeconds: number): number {
  return Math.floor(Math.floor(atMs / 1000) / effectiveWindow(windowSeconds));
}

/** Milliseconds from `atMs` until the window after the one covering it begins — when the code next changes. */
export function msUntilNextWindow(atMs: number, windowSeconds: number): number {
  const windowMs = effectiveWindow(windowSeconds) * 1000;
  return (windowIndexAt(atMs, windowSeconds) + 1) * windowMs - atMs;
}

/** The 8-byte big-endian counter RFC 4226 feeds the HMAC. */
export function counterBytes(counter: number): Uint8Array {
  const bytes = new Uint8Array(8);
  new DataView(bytes.buffer).setBigUint64(0, BigInt(counter));
  return bytes;
}

/**
 * RFC 4226 §5.3 dynamic truncation: the low nibble of the last MAC byte picks a 4-byte window, its top bit is
 * masked off, and the result is reduced to `digits` decimal digits with leading zeros kept.
 */
export function truncate(mac: Uint8Array, digits: number = ROTATING_CODE_DIGITS): string {
  const offset = mac[mac.length - 1] & 0x0f;
  const bin = ((mac[offset] & 0x7f) << 24) | (mac[offset + 1] << 16) | (mac[offset + 2] << 8) | mac[offset + 3];
  return (bin % 10 ** digits).toString().padStart(digits, '0');
}

/** The compound `{sessionId}.{rotating}` the QR's `code` field carries. Mirrors `EnrollmentCode.Compose`. */
export function composeEnrollmentCode(sessionId: string, rotating: string): string {
  return `${sessionId}${ENROLLMENT_CODE_SEPARATOR}${rotating}`;
}

/**
 * The exact string the QR encodes: compact JSON, `host` first, `code` second — the byte-for-byte shape
 * `Shared.Core.Models.Device.DeviceEnrollmentPayload.Serialize()` produces, because the register's paste
 * fallback accepts exactly what its camera would have decoded and nothing looser.
 */
export function enrollmentPayload(host: string, code: string): string {
  return JSON.stringify({host, code});
}

/**
 * The rotating digits for one window, over Web Crypto. Exported so the golden-vector spec can pin the
 * algorithm without a component in the way.
 *
 * @param secretBase32 the session secret as the server returned it.
 * @param counter the window index (see {@link windowIndexAt}).
 * @param subtle the crypto implementation — injectable for the one spec that must prove a failure path.
 */
export async function rotatingDigits(secretBase32: string, counter: number,
                                     subtle: SubtleCrypto = crypto.subtle): Promise<string> {
  const key = await subtle.importKey('raw', base32Decode(secretBase32), {name: 'HMAC', hash: 'SHA-256'}, false,
    ['sign']);
  const mac = new Uint8Array(await subtle.sign('HMAC', key, counterBytes(counter)));
  return truncate(mac, ROTATING_CODE_DIGITS);
}

function effectiveWindow(windowSeconds: number): number {
  return windowSeconds > 0 ? windowSeconds : DEFAULT_WINDOW_SECONDS;
}

/**
 * Computes the compound enrolment code a session shows at an instant. The one seam the rotating panel has
 * around the asynchronous HMAC — see the file header for why it is a service and not a plain function.
 */
@Injectable({providedIn: 'root'})
export class RotatingEnrollmentCodeService {
  /**
   * The `{sessionId}.{rotating}` code for the window covering `atMs`.
   *
   * @param session the opened enrolment session (`secret` is consumed here and nowhere else).
   * @param atMs the instant to compute for; defaults to now.
   */
  async codeAt(session: Pick<DeviceEnrollmentSession, 'sessionId' | 'secret' | 'windowSeconds'>,
               atMs: number = Date.now()): Promise<string> {
    const rotating = await rotatingDigits(session.secret, windowIndexAt(atMs, session.windowSeconds));
    return composeEnrollmentCode(session.sessionId, rotating);
  }
}
